import { FileMinusIcon, PlusIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import { useCreditNotesForInvoice } from "@/features/credit-notes/use-credit-note-list"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/features/auth/auth-context"
import { useFormatDay } from "@/lib/dates"
import type { InvoiceDetail } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * What has been credited against THIS bill, and the way to credit more (§17.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS IS THE ENTRY POINT TO THE WHOLE FEATURE, AND IT IS THE ONLY ONE.
 *
 * §17.3 rule 1: a credit note is raised FROM the bill it corrects. There is no
 * "New credit note" item in Quick Create, none in the search palette's Create
 * list, and no `/credit-notes/new` path at all — because a blank form asking
 * which invoice is being reduced is a form on which the wrong invoice can be
 * chosen, on the single document whose entire job is to name another. The button
 * lives here, on a bill, where the answer cannot be wrong.
 *
 * IT IS ON THE BILL'S SCREEN RATHER THAN IN ITS MENU because §11.3's whole
 * argument about an issued invoice applies: the owner arrives asking "this is
 * wrong, how do I fix it?", and the two legal answers — cancel, or credit —
 * belong where he is looking. Cancelling is the destructive card at the foot;
 * this is the constructive one, above it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE FIGURES HERE ARE, AND WHERE THEY COME FROM.
 *
 * THE PER-NOTE AMOUNTS are each note's own `grandTotalPaise`, positive, as the
 * server stored them. NOTHING ON THIS CARD SUMS THEM. "How much is off this
 * bill" is answered by `summary.creditedPaise` on the PAYMENTS card directly
 * below, which the SERVER computes over ISSUED notes only — and a second total
 * added up here would be a second opinion that disagrees the moment a note is
 * cancelled. §17.5 already paid for that lesson once.
 *
 * CANCELLED NOTES ARE LISTED, and their badge says so. A bill that was credited
 * and then un-credited is a story that has to be readable on the bill itself,
 * and the money is already back in the outstanding figure below.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * IT RENDERS NOTHING AT ALL when the caller holds neither capability, when the
 * module is off (its query 404s — §18.3, and the flag ships DISABLED), or when
 * there is nothing to show and nothing to offer. A card that only ever says "no
 * credit notes" on every bill in the book is a row the eye learns to skip.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function CreditNotesOnInvoice({
  invoice,
}: {
  invoice: InvoiceDetail
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()
  const formatDay = useFormatDay()

  /*
   * BOTH CAPABILITIES ARE CHECKED, AND THEY ARE DIFFERENT QUESTIONS (§2.1).
   * `creditnote.view` is the shared read row — the ACCOUNTANT holds it, because
   * she files the CDNR section of GSTR-1 — while `creditnote.create` is the
   * author's. Someone may hold either without the other, and the server enforces
   * both again regardless; these hide controls and never authorise one.
   */
  const mayView = can("creditnote.view")
  const mayCreate = can("creditnote.create")

  /*
   * Only ISSUED bills can be credited (rule 4), and the SERVER refuses the rest
   * with a 409 naming which. A DRAFT has billed nothing — there is no liability
   * to reduce — and a CANCELLED bill has been withdrawn in full already. The
   * button is HIDDEN rather than disabled: a greyed control invites hunting for
   * the way to un-grey it, and there is no way.
   */
  const creditable = invoice.status === "ISSUED"

  const query = useCreditNotesForInvoice(mayView ? invoice.id : undefined)
  const notes = query.data?.data ?? []

  // Nothing to list and nothing to offer — render nothing at all.
  if (notes.length === 0 && !(mayCreate && creditable)) return null

  return (
    <Card data-testid="invoice-credit-notes">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <FileMinusIcon aria-hidden="true" className="size-4 shrink-0" />
          {t("creditNote.onBillTitle")}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {notes.length === 0 ? (
          /*
           * "NOTHING HAS BEEN TAKEN OFF THIS BILL" IS A CLAIM, so it is only
           * made when the server actually said so. While the module is off the
           * query 404s (§18.3) — it did not answer "none", it refused to answer
           * — and asserting emptiness there would state as fact something this
           * screen cannot know. It says nothing instead, and never "you do not
           * have permission", which is the leak the 404 exists to avoid.
           */
          query.isError ? null : (
            <p className="text-muted-foreground text-sm">{t("creditNote.onBillNoneYet")}</p>
          )
        ) : (
          <ul className="flex flex-col divide-y rounded-lg border">
            {notes.map((note) => (
              <li key={note.id} className="flex items-center justify-between gap-3 p-3">
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <Link
                      to={routes.creditNote(note.id)}
                      className="hover:text-primary font-medium tabular-nums underline-offset-4 hover:underline"
                    >
                      {note.number}
                    </Link>
                    <DocumentStatusBadge
                      status={note.status}
                      kind="creditNote"
                      className="text-xs"
                    />
                  </span>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {formatDay(note.date)}
                  </span>
                  <span className="text-muted-foreground line-clamp-2 text-xs">{note.reason}</span>
                </span>
                {/* POSITIVE, NO MINUS SIGN (§17.3). What it means is said by the
                  * card's heading and by the payments card's "Credited back"
                  * row directly below, not by a sign on the number. */}
                <span className="shrink-0 font-medium tabular-nums">
                  {formatRupees(note.grandTotalPaise)}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {mayCreate && creditable ? (
            <Button nativeButton={false} render={<Link to={routes.creditNoteFor(invoice.id)} />}>
              <PlusIcon />
              {t("creditNote.raiseAction")}
            </Button>
          ) : null}

          {/*
            * "Every figure is tappable and leads somewhere useful" (§11.2). The
            * register narrowed to this bill is the same query this card reads,
            * with paging and the rest of the filters available — which is where
            * a bill with a dozen notes against it becomes readable.
            */}
          {notes.length === 0 ? null : (
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.creditNotesForInvoice(invoice.id)} />}
            >
              {t("creditNote.openRegisterForBill")}
            </Button>
          )}
        </div>

        {/*
          * SAID IN WORDS, BECAUSE THE AMOUNTS ABOVE ARE POSITIVE. Without this
          * a column of rupee figures under a bill reads as more money owed.
          */}
        {notes.length === 0 ? null : (
          <p className="text-muted-foreground text-xs">{t("creditNote.onBillHelp")}</p>
        )}
      </CardContent>
    </Card>
  )
}
